import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Card } from '@atoms/index';
import { paymentService } from '@services/paymentService';
import { useAuthStore } from '@store/authStore';

const PaymentResultPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handlePaymentResult = async () => {
      const token = searchParams.get('token');
      const orderId = searchParams.get('orderId');
      const orderNumber = searchParams.get('orderNumber');
      const trackingToken = searchParams.get('trackingToken');
      const status = searchParams.get('status');
      const errorParam = searchParams.get('error');

      const redirectGuestToTracking = () => {
        if (trackingToken) {
          navigate(`/siparis-takip?token=${encodeURIComponent(trackingToken)}`, { replace: true });
          return;
        }

        const confirmationParams = new URLSearchParams({
          orderNumber: orderNumber || '',
        });
        navigate(`/checkout/confirmation?${confirmationParams.toString()}`, { replace: true });
      };

      if (status === 'success') {
        setShowSuccessAnimation(true);

        if (orderId) {
          sessionStorage.setItem(`payment_success_${orderId}`, Date.now().toString());

          setIsRedirecting(true);
          setTimeout(() => {
            if (isAuthenticated) {
              navigate(`/orders/${orderId}`, {
                replace: true,
                state: { showSuccessAnimation: true },
              });
            } else {
              redirectGuestToTracking();
            }
          }, 3000);

          if (token) {
            paymentService.verifyPayment(token).catch((err) => {
              console.warn('Background payment verification failed (non-critical):', err);
            });
          }

          return;
        }

        if (orderNumber) {
          try {
            const { orderService } = await import('@services/orderService');
            const orders = await orderService.getUserOrders();
            const order = orders.find((o) => o.orderNumber === orderNumber);

            if (order) {
              sessionStorage.setItem(`payment_success_${order.id}`, Date.now().toString());
              setIsRedirecting(true);
              setTimeout(() => {
                if (isAuthenticated) {
                  navigate(`/orders/${order.id}`, {
                    replace: true,
                    state: { showSuccessAnimation: true },
                  });
                } else {
                  redirectGuestToTracking();
                }
              }, 3000);

              if (token) {
                paymentService.verifyPayment(token).catch((err) => {
                  console.warn('Background payment verification failed (non-critical):', err);
                });
              }
              return;
            }
          } catch (err) {
            console.error('Failed to find order:', err);
            // Fall through to token verification
          }
        }

        // If only token is provided, verify in background but show success immediately
        if (token) {
          // Show success animation while verifying
          setIsRedirecting(true);
          
          // Background verification (non-blocking)
          paymentService.verifyPayment(token)
            .then((result) => {
              if (result.success && result.orderId) {
                sessionStorage.setItem(`payment_success_${result.orderId}`, Date.now().toString());
                setTimeout(() => {
                  if (isAuthenticated) {
                    navigate(`/orders/${result.orderId}`, {
                      replace: true,
                      state: { showSuccessAnimation: true },
                    });
                  } else {
                    redirectGuestToTracking();
                  }
                }, 3000);
              } else {
                // Verification failed, but we already showed success
                // This shouldn't happen if backend redirects correctly
                console.error('Payment verification failed:', result.error);
                setError(result.error || 'Ödeme doğrulaması başarısız oldu.');
                setIsRedirecting(false);
                setShowSuccessAnimation(false);
              }
            })
            .catch((err) => {
              console.error('Payment verification error:', err);
              setError('Ödeme doğrulaması sırasında bir hata oluştu.');
              setIsRedirecting(false);
              setShowSuccessAnimation(false);
            });
          return;
        }

        // If status=success but no identifiers, show error
        setError('Geçersiz ödeme yönlendirmesi. Sipariş bilgisi bulunamadı.');
        setIsRedirecting(false);
        setShowSuccessAnimation(false);
        return;
      }

      // Check for error status
      if (status === 'failure' || errorParam) {
        const errorMessage = errorParam || 'Ödeme işlemi başarısız oldu.';
        setError(decodeURIComponent(errorMessage));
        return;
      }

      // No status parameter - invalid redirect
      setError('Geçersiz ödeme yönlendirmesi.');
    };

    handlePaymentResult();
  }, [searchParams, navigate, isAuthenticated]);

  if (showSuccessAnimation) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center max-w-md">
            {/* Success Animation - Green Tick */}
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 animate-bounce">
              <svg
                className="w-12 h-12 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Başarılı!</h2>
            <p className="text-gray-600 mb-4">
              {isRedirecting
                ? 'Siparişiniz alındı. Sipariş takip sayfasına yönlendiriliyorsunuz...'
                : 'Siparişiniz alındı ve hazırlanmaya başlandı.'}
            </p>
            {isRedirecting && (
              <div className="mt-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Yönlendiriliyorsunuz...</p>
              </div>
            )}
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center max-w-md">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Ödeme Hatası</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/checkout')}
              className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Ödeme Sayfasına Dön
            </button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return null;
};

export default PaymentResultPage;
